import {
  Alert,
  Button,
  Form,
  FormInstance,
  Icons,
  Input,
  Radio,
  Select,
  Space,
} from '@pankod/refine-antd'
import { useOne } from '@pankod/refine-core'
import React from 'react'

import ContentForm from '@components/ContentBank/ContentForm'
import { TFilterOptions } from '@components/DataTableParticipants/types'
import { InfiniteSelect } from '@resources/lms-management/components/InfiniteSelect'
import PreviewDetailLandingPage from '@resources/lms-management/landing-page/blocks/PreviewDetailLandingPage'
import { validateWhitelistEmailDomains } from '@resources/lms-management/landing-page/utils'

export type BlueprintData = {
  imageUrl: string
  title: string
  description: string
}

type SelfEnrollmentLandingPageFormFieldsProps = {
  form: FormInstance
  visible: boolean
  blueprintData: BlueprintData
  initialBlueprintOption?: { label: string; value: string | number }
  totalWhitelistedUsers?: number
  whitelistUserCsvFile?: File
  whitelistUserCsvError?: string
  isDownloadingWhitelistUsers?: boolean
  onBlueprintSelect: (data: BlueprintData) => void
  onWhitelistUserCsvChange?: (file: File) => void
  onDownloadWhitelistUsers?: () => void
}

const SelfEnrollmentLandingPageFormFields = ({
  form,
  visible,
  blueprintData,
  initialBlueprintOption,
  totalWhitelistedUsers = 0,
  whitelistUserCsvFile,
  whitelistUserCsvError,
  isDownloadingWhitelistUsers = false,
  onBlueprintSelect,
  onWhitelistUserCsvChange,
  onDownloadWhitelistUsers,
}: SelfEnrollmentLandingPageFormFieldsProps) => {
  const category = Form.useWatch('category', form)
  const whitelistCsvInputRef = React.useRef<HTMLInputElement>(null)

  const { data: filterOptions } = useOne<TFilterOptions>({
    resource: 'programs/participants',
    id: 'filter-options',
    dataProviderName: 'lms',
    queryOptions: {
      enabled: visible,
    },
  })

  const provinceOptions =
    filterOptions?.data?.regions
      ?.map((province) => ({
        label: province.name,
        value: province.kode,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) || []

  return (
    <div className="flex gap-6">
      <div className="w-full min-w-0">
        <div className="grid grid-cols-1">
          <Form.Item
            label="Blueprint"
            name="blueprintCourseId"
            rules={[{ required: true }]}
          >
            <InfiniteSelect
              optionLabel="title"
              optionValue="courseId"
              resource="diklat/blueprints"
              visible={visible}
              searchParams="nameOrCourseCode"
              onSelect={(data) => onBlueprintSelect(data?.data)}
              placeholder="Pilih blueprint"
              initialOptionValue={initialBlueprintOption}
            />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Form.Item label="Judul" name="title">
              <Input placeholder="Tulis judul" />
            </Form.Item>
            <Form.Item label="Deskripsi" name="description">
              <Input.TextArea
                rows={2}
                className="resize-none"
                placeholder="Tulis deskripsi"
              />
            </Form.Item>
          </div>
          <div>
            <Form.Item label="Judul Default">
              <Input disabled value={blueprintData.title} />
            </Form.Item>
            <Form.Item label="Deskripsi Default">
              <Input.TextArea
                rows={2}
                className="resize-none"
                disabled
                value={blueprintData.description}
              />
            </Form.Item>
          </div>
        </div>

        <Form.Item label="Detail Diklat" name="detail">
          <ContentForm
            index="program-showcase"
            showTitle={false}
            showDeleteButton={false}
            showIndentation={false}
            showHeader={[
              { label: 'Title', value: '4' },
              { label: 'Normal', value: '' },
            ]}
            editorClassName="max-h-[260px] overflow-auto"
          />
        </Form.Item>

        <Form.Item label="Teks Badge" name="badge">
          <Input placeholder="Tulis teks pada badge" />
        </Form.Item>

        <Form.Item
          label="Kategori Tampilan"
          name="category"
          initialValue="PUBLIC"
          rules={[{ required: true }]}
        >
          <Radio.Group>
            <Space direction="vertical">
              <Radio value="PUBLIC">Publik</Radio>
              <Radio value="PROVINCE">Provinsi Tertentu</Radio>
              <Radio value="WHITELIST_USER">
                User Tertentu (<em>Whitelisted</em>)
              </Radio>
              <Radio value="WHITELIST_EMAIL_DOMAIN">
                Domain Email (<em>Whitelisted</em>)
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {category === 'PROVINCE' && (
          <Form.Item
            label="Pilih Provinsi"
            name="provinceCodes"
            rules={[
              {
                required: true,
                message: 'Harus memilih setidaknya satu provinsi',
              },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Pilih provinsi"
              placement="topRight"
              options={provinceOptions}
              filterOption={(input, option) =>
                (option?.label as unknown as string)
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
        )}

        {category === 'WHITELIST_USER' && (
          <>
            <Form.Item
              label="Email User Whitelist"
              validateStatus={whitelistUserCsvError ? 'error' : undefined}
              help={whitelistUserCsvError}
            >
              <Space>
                <Button
                  type="primary"
                  icon={<Icons.UploadOutlined />}
                  onClick={() => whitelistCsvInputRef.current?.click()}
                >
                  Unggah Berkas CSV
                </Button>
                <Button
                  icon={<Icons.DownloadOutlined />}
                  loading={isDownloadingWhitelistUsers}
                  onClick={onDownloadWhitelistUsers}
                >
                  {totalWhitelistedUsers === 0
                    ? 'Unduh Template CSV'
                    : 'Unduh Berkas CSV'}
                </Button>
              </Space>
              <input
                ref={whitelistCsvInputRef}
                aria-label="Unggah Berkas CSV"
                style={{ display: 'none' }}
                type="file"
                accept=".csv"
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  if (file) {
                    onWhitelistUserCsvChange?.(file)
                  }
                  event.target.value = ''
                }}
              />
              <p className="mt-2 mb-0 text-sm text-gray-600">
                {totalWhitelistedUsers.toLocaleString('id-ID')} email sudah
                terdaftar.
              </p>
              {whitelistUserCsvFile && (
                <p className="mt-1 mb-0 text-sm">
                  Berkas dipilih: {whitelistUserCsvFile.name}
                </p>
              )}
            </Form.Item>
            <Alert
              type="info"
              message={
                <>
                  CSV harus memiliki tepat satu header &quot;user_emails&quot;
                  dan satu email pada setiap baris yang tidak kosong. Baris
                  kosong akan diabaikan. Mengunggah berkas baru akan mengganti
                  seluruh daftar email whitelist.
                </>
              }
            />
          </>
        )}

        {category === 'WHITELIST_EMAIL_DOMAIN' && (
          <>
            <Form.Item
              label="Domain Email Whitelist"
              name="whitelistEmailDomains"
              rules={[
                {
                  required: true,
                  message: 'Harus mengisi setidaknya satu domain email',
                },
                {
                  validator: validateWhitelistEmailDomains,
                },
              ]}
            >
              <Select
                mode="tags"
                placeholder="Ketik domain email dan tekan enter"
                tokenSeparators={[',', ' ']}
                open={false}
                allowClear
              />
            </Form.Item>
            <Alert
              type="info"
              message={
                <>
                  Setiap domain email harus diawali dengan &quot;@&quot;. Anda
                  dapat <em>paste</em> daftar domain yang dipisahkan dengan
                  koma.
                </>
              }
            />
          </>
        )}
      </div>

      <div className="flex-shrink-0">
        <PreviewDetailLandingPage form={form} blueprintData={blueprintData} />
      </div>
    </div>
  )
}

export default SelfEnrollmentLandingPageFormFields
